import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import axiosInstanceSynonyms from "../axiosInstanceSynonyms";

const read = (data, camel, pascal) => data?.[camel] ?? data?.[pascal];
const normalizeNodes = (nodes) => (Array.isArray(nodes) ? nodes : []).map((node) => ({
  id: read(node, "id", "Id"), name: read(node, "name", "Name"),
  children: normalizeNodes(read(node, "children", "Children")),
}));

const CategoryNode = ({ node, parentId, exclusions, onToggle, disabled }) => {
  const excluded = parentId && (exclusions[parentId] || []).includes(node.id);
  const label = parentId ? <FormControlLabel sx={{ m: 0 }} label={node.name}
    control={<Checkbox size="small" checked={!excluded} disabled={disabled}
      onChange={(_, enabled) => onToggle(parentId, node.id, enabled)} />} />
    : <Typography component="span" sx={{ fontWeight: 700 }}>{node.name} (root)</Typography>;
  return <Box component="li" sx={{ listStyle: "none" }}>
    {node.children.length ? <Box component="details">
      <Box component="summary" sx={{ cursor: "pointer", py: .25 }}>{label}</Box>
      <Box component="ul" sx={{ m: 0, pl: 3, borderLeft: "1px solid #e2e8f0" }}>
        {node.children.map((child) => <CategoryNode key={child.id} node={child} parentId={node.id} exclusions={exclusions} onToggle={onToggle} disabled={disabled} />)}
      </Box>
    </Box> : label}
  </Box>;
};

export default function StorefrontTaxonomyExclusions({ tenantId, revision, savedIndex, indexChanged, exclusions, onChange, disabled }) {
  const [tree, setTree] = useState(null);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let ignore = false;
    setTree(null);
    setError("");
    if (!savedIndex || revision === null || revision === undefined) return undefined;
    axiosInstanceSynonyms.get(`/api/tenant/${encodeURIComponent(tenantId)}/storefront-taxonomy`).then(({ data }) => {
      if (ignore) return;
      if (read(data, "revision", "Revision") !== revision || read(data, "taxonomyIndexName", "TaxonomyIndexName") !== savedIndex) {
        setError("The taxonomy configuration changed. Reload settings before editing exclusions.");
        return;
      }
      setTree(normalizeNodes(read(data, "categories", "Categories")));
    }).catch(() => {
      if (!ignore) setError("Could not load the taxonomy. Check the configured index and try again.");
    });
    return () => { ignore = true; };
  }, [tenantId, revision, savedIndex, refresh]);

  const selected = exclusions || {};
  const count = Object.values(selected).reduce((total, children) => total + children.length, 0);
  const toggle = (parent, child, enabled) => {
    const children = new Set(selected[parent] || []);
    if (enabled) children.delete(child); else children.add(child);
    const next = { ...selected };
    if (children.size) next[parent] = [...children]; else delete next[parent];
    onChange(next);
  };
  return <Stack spacing={1}>
    <Typography sx={{ fontWeight: 800 }}>Taxonomy exclusions</Typography>
    <Typography variant="body2" color="text.secondary">Uncheck a child to exclude its branch when shoppers search for its parent or an ancestor. Direct searches for that child still work. Nested toggles keep their own selections even inside an excluded branch.</Typography>
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2">{count} excluded children</Typography>
      <Button size="small" disabled={disabled || !count} onClick={() => onChange({})}>Clear exclusions</Button>
    </Stack>
    {indexChanged ? <Alert severity="info">Save the taxonomy index and reload its tree before editing child selections. Existing exclusions must be valid in the new taxonomy; clear them first if they should not carry over.</Alert>
      : !savedIndex ? <Alert severity="info">Configure and save a taxonomy index to select excluded children.</Alert>
      : error ? <Alert severity="error" action={<Button disabled={disabled} onClick={() => setRefresh((value) => value + 1)}>Retry</Button>}>{error}</Alert>
      : tree === null ? <Typography variant="body2">Loading taxonomy…</Typography>
      : tree.length === 0 ? <Typography variant="body2">No categories were returned for this taxonomy.</Typography>
      : <Box component="ul" aria-label="Taxonomy categories" sx={{ m: 0, p: 1, maxHeight: 520, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 2 }}>
        {tree.map((node) => <CategoryNode key={node.id} node={node} exclusions={selected} onToggle={toggle} disabled={disabled} />)}
      </Box>}
  </Stack>;
}
